import { Select, Tooltip, Typography } from "antd";
import { LuArrowUp, LuArrowDown } from "react-icons/lu";
import {
  GROUPBY_OPTIONS,
  getAvailableSortKinds,
  type ChartSortOption,
  type GroupByOption,
  type SortKindDisplay,
} from "../constants";

const { Text } = Typography;

interface SortSeriesOption {
  id: string;
  label: string;
}

interface SortControl {
  value: ChartSortOption | null;
  onValueChange: (v: ChartSortOption | null) => void;
  seriesOptions?: SortSeriesOption[];
  seriesId?: string | null;
  onSeriesIdChange?: (id: string) => void;
}

interface GroupByBarProps {
  value: GroupByOption;
  onChange: (groupBy: GroupByOption) => void;
  sort?: SortControl;
  /** Group-bys this widget cannot use, with the reason shown on hover. A count cannot
   *  group by drill: none of the counted tables carries a drill relation, and the
   *  backend rejects the query rather than returning the same total per drill. */
  disabledGroupBys?: Partial<Record<GroupByOption, string>>;
}

function getSortTooltip(kind: SortKindDisplay, isAsc: boolean): string {
  const dir = isAsc ? "increasing" : "decreasing";
  const dirAlpha = isAsc ? "(A-Z)" : "(Z-A)";
  const dirValue = isAsc ? "(low to high)" : "(high to low)";
  if (kind.kind === "value") return `Sorts values in ${dir} order ${dirValue}`;
  if (kind.kind === "position") return `Sorts by position alphabetically in ${dir} order ${dirAlpha}`;
  const subject = kind.label === "Drill" ? "drill names" : "player names";
  return `Sorts ${subject} alphabetically in ${dir} order ${dirAlpha}`;
}

function handleKindClick(
  kind: SortKindDisplay,
  currentSort: ChartSortOption | null,
  onValueChange: (v: ChartSortOption | null) => void,
) {
  if (currentSort === kind.ascValue) {
    onValueChange(kind.descValue);
  } else if (currentSort === kind.descValue) {
    onValueChange(null);
  } else {
    onValueChange(kind.ascValue);
  }
}

export function GroupByBar({ value, onChange, sort, disabledGroupBys }: GroupByBarProps) {
  const sortKinds = sort ? getAvailableSortKinds(value) : [];
  const isValueSort = sort?.value?.startsWith("value-") ?? false;
  const multiSeries = (sort?.seriesOptions?.length ?? 0) > 1;
  return (
    <div className="awm-groupby-bar">
      <div className="awm-groupby-row">
        <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
          Group by
        </Text>
        <div className="awm-groupby-options">
          {GROUPBY_OPTIONS.map((opt, i) => {
            const prevSection = i > 0 ? GROUPBY_OPTIONS[i - 1].section : null;
            const showDivider = prevSection != null && prevSection !== opt.section;
            const disabledReason = disabledGroupBys?.[opt.value];
              const chip = (
                <button
                  className={`awm-groupby-chip${value === opt.value ? " active" : ""}`}
                  disabled={!!disabledReason}
                  onClick={() => onChange(opt.value)}
                >
                  {opt.label}
                </button>
              );
              return (
              <span key={opt.value} style={{ display: "inline-flex", alignItems: "center" }}>
                {showDivider && <span className="awm-groupby-divider" />}
                {disabledReason ? <Tooltip title={disabledReason}>{chip}</Tooltip> : chip}
              </span>
            );
          })}
        </div>
      </div>

      {sort && (
        <div className="awm-sort-row">
          <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
            Sort by
          </Text>
          <div className="awm-groupby-options">
            {sortKinds.map((kind) => {
              const isAsc = sort.value === kind.ascValue;
              const isDesc = sort.value === kind.descValue;
              const isActive = isAsc || isDesc;
              const tooltip = isAsc
                ? getSortTooltip(kind, true)
                : isDesc
                ? getSortTooltip(kind, false)
                : "";
              return (
                <Tooltip key={kind.kind} title={tooltip}>
                  <button
                    className={`awm-groupby-chip${isActive ? " active" : ""}`}
                    onClick={() => handleKindClick(kind, sort.value, sort.onValueChange)}
                  >
                    {kind.label}
                    {isAsc && <LuArrowUp style={{ marginLeft: 4, fontSize: 11, verticalAlign: "middle" }} />}
                    {isDesc && <LuArrowDown style={{ marginLeft: 4, fontSize: 11, verticalAlign: "middle" }} />}
                  </button>
                </Tooltip>
              );
            })}
          </div>

          {isValueSort && multiSeries && (
            <>
              <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                by
              </Text>
              <Select
                size="small"
                value={sort.seriesId ?? sort.seriesOptions![0]?.id}
                onChange={(id) => sort.onSeriesIdChange?.(id)}
                style={{ flex: 1, minWidth: 0 }}
                options={sort.seriesOptions!.map((o) => ({ value: o.id, label: o.label }))}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
